import { MutableRefObject, useCallback, useEffect, useState } from 'react';

/**
 * Hook to calculate the available vertical distance from a ref element to the
 * bottom of its container (modal or viewport).
 *
 * IMPORTANT BUG FIX (NEX-20062):
 * Prior to this fix, refPosition was captured OUTSIDE the onResize callback,
 * causing it to become stale. When content loaded dynamically (e.g., database
 * destinations adding sections), the ref's position would change but the hook
 * would use the old cached position, resulting in incorrect height calculations.
 *
 * FIX: refPosition is now recalculated INSIDE onResize on every call, ensuring
 * we always use the current DOM position.
 *
 * @param ref - Reference to the element to measure from
 * @param defaultHeight - Initial height before calculation (default: 0)
 * @param from - Measure from 'top' or 'bottom' of the ref element (default: 'bottom')
 * @returns Available distance in pixels
 */
export function useDistance(
  ref: MutableRefObject<HTMLElement>,
  defaultHeight = 0,
  from: 'top' | 'bottom' = 'bottom',
) {
  const [distance, setDistance] = useState(defaultHeight);

  const onResize = useCallback(() => {
    if (!ref?.current) return;

    // CRITICAL: Calculate position inside callback to avoid stale values
    // This ensures we get the current DOM position even if layout has changed
    const refPosition = ref?.current?.getBoundingClientRect()[from];

    // Check if ref is inside a MUI modal/dialog
    let modalBounds = null;

    for (let parent = ref?.current?.parentElement; parent; parent = parent?.parentElement) {
      if (parent.classList.contains('MuiDialog-paper')) {
        modalBounds = parent.firstElementChild?.getBoundingClientRect();
        break;
      }
    }

    // Calculate available height:
    // - In modal: use modal height
    // - Full page: use window height
    const parentHeight = modalBounds ? modalBounds.height : window.innerHeight;

    // Distance = container height - (ref position - container top)
    setDistance(parentHeight - ((refPosition || 0) - (modalBounds?.top || 0)));
  }, [ref, from]);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [onResize]);

  return distance;
}
